import {
    ActionContext,
    ActionTree,
    GetterTree,
    MutationTree,
    Module,
    Store as VuexStore,
    CommitOptions,
    DispatchOptions,
} from 'vuex'

import store, { State as RootState } from '@/store'

import {Favorite, Folder, Gallery, GalleryOption, ShortGallery} from "@/types/GalleryType";

import {
    deleted,
    deleteGallery,
    details,
    favorites,
    folders,
    list,
    my,
    pinGallery,
    sortFolders, unpinGallery
} from '@/data/requests/gallery';

// Declare state
export type State = {
    galleries: ShortGallery[],
    galleriesPage: number,
    galleriesHasMore: boolean,
    galleriesTotal: number,
    galleriesList: GalleryOption[],
    gallery: Gallery | null,

    folders: Folder[],

    favorites: Favorite[],

    deletedGalleries: ShortGallery[],
}

// Mutation contracts
export type Mutations<S = State> = {
    // setUser(state: S, { user }: { user: User }): void,
    setGalleries(state: S, {galleries}: {galleries: ShortGallery[]}): void,
    appendGalleries(state: S, {galleries}: {galleries: ShortGallery[]}): void,
    setGalleriesPagination(state: S, {page, hasMore, total}: {page: number, hasMore: boolean, total: number}): void,
    setGalleriesList(state: S, {galleries}: {galleries: GalleryOption[]}): void,
    removeGallery(state: S, {gallery}: {gallery: string}): void,
    setGallery(state: S, {gallery}: {gallery: Gallery}): void,
    setFolders(state: S, {folders}: {folders: Folder[]}): void,
    setGalleryOrder(state: S, {type}: {type: string}): void,
    setFolderOrder(state: S, {folder, type}: {folder: string, type: string}): void,
    addPhotoToFolder(state: S, {folder}: {folder: string}): void,
    removePhotoFromFolder(state: S, {folder}: {folder: string}): void,
    addPhotoToGallery(state: S, {filesize, count}: {filesize: number, count: number}): void,
    removePhotoFromGallery(state: S, {filesize, count}: {filesize: number, count: number}): void,
    setFavorites(state: S, {favorites}: {favorites: Favorite[]}): void,
    addDeletedToGallery(state: S, {count}: {count: number}): void,
    removeDeletedFromGallery(state: S, {count}: {count: number}): void,
    clearDeletedFromGallery(state: S): void,
    setDeletedGalleries(state: S, {galleries}: {galleries: ShortGallery[]}): void,
    removeDeletedGallery(state: S, {gallery}: {gallery: string}): void,
    setPinnedGallery(state: S, {is_pinned, gallery}: {is_pinned: boolean, gallery: string}): void,
}

// Actions context
type AugmentedActionContext = {
    commit<K extends keyof Mutations>(
        key: K,
        payload: Parameters<Mutations[K]>[1],
    ): ReturnType<Mutations[K]>
} & Omit<ActionContext<State, RootState>, 'commit'>

// Actions contracts
export interface Actions {
    loadGalleries(
        { commit }: AugmentedActionContext,
        payload: {page?: number, sort?: string, search?: string, append?: boolean}
    ): Promise<any>
    loadGalleriesList(
        { commit }: AugmentedActionContext,
        payload: {}
    ): Promise<any>
    loadDeletedGalleries(
        { commit }: AugmentedActionContext,
        payload: {}
    ): Promise<any>
    loadGallery(
        { commit }: AugmentedActionContext,
        payload: {slug: string},
    ): Promise<any>
    loadFolders(
        { commit }: AugmentedActionContext,
        payload: {gallery: string},
    ): Promise<any>
    sortFolders(
        { commit }: AugmentedActionContext,
        payload: {gallery: string, folders: Folder[]}
    ): Promise<any>
    loadFavorites(
        { commit }: AugmentedActionContext,
        payload: {gallery: string},
    ): Promise<any>
    deleteGallery(
        { commit }: AugmentedActionContext,
        payload: {gallery: string},
    ): Promise<any>
    pinGallery(
        { commit }: AugmentedActionContext,
        payload: {gallery: string},
    ): Promise<any>
    unpinGallery(
        { commit }: AugmentedActionContext,
        payload: {gallery: string},
    ): Promise<any>
}

// Getters types
export type Getters = {
    galleries(state: State): ShortGallery[],
    galleriesHasMore(state: State): boolean,
    galleriesPage(state: State): number,
    galleriesTotal(state: State): number,
    galleriesList(state: State): GalleryOption[],
    gallery(state: State): Gallery | null,
    hasGalleries(state: State): boolean,
    folders(state: State): Folder[],
    favorites(state: State): Favorite[],
    deletedGalleries(state: State): ShortGallery[],
}

const state: State = {
    galleries: [],
    galleriesPage: 1,
    galleriesHasMore: false,
    galleriesTotal: 0,
    galleriesList: [],
    gallery: null,

    folders: [],
    favorites: [],

    deletedGalleries: [],
};

const getters: GetterTree<State, RootState> & Getters = {
    galleries: (state) => {
        return state.galleries;
    },

    galleriesHasMore: (state) => {
        return state.galleriesHasMore;
    },

    galleriesPage: (state) => {
        return state.galleriesPage;
    },

    galleriesTotal: (state) => {
        return state.galleriesTotal;
    },

    galleriesList: (state) => {
        return state.galleriesList;
    },

    gallery: (state) => {
        return state.gallery;
    },

    hasGalleries(state): boolean {
        return state.galleries.length != 0;
    },

    folders(state): Folder[] {
        return state.folders;
    },

    favorites(state): Favorite[] {
        return state.favorites;
    },

    deletedGalleries(state): ShortGallery[] {
        return state.deletedGalleries;
    }
};

const mutations: MutationTree<State> & Mutations = {
    setGalleries(state, {galleries}) {
        state.galleries = galleries;
    },

    appendGalleries(state, {galleries}) {
        state.galleries = state.galleries.concat(galleries);
    },

    setGalleriesPagination(state, {page, hasMore, total}) {
        state.galleriesPage = page;
        state.galleriesHasMore = hasMore;
        state.galleriesTotal = total;
    },

    setGalleriesList(state, {galleries}) {
        state.galleriesList = galleries;
    },

    removeGallery(state, {gallery}) {
        state.galleries = state.galleries.filter(item => item.slug != gallery);
    },

    setDeletedGalleries(state, {galleries}) {
        state.deletedGalleries = galleries
    },

    setGallery(state, {gallery}) {
        state.gallery = gallery;
    },

    setFolders(state, {folders}) {
        state.folders = folders;
    },

    setGalleryOrder(state, {type}) {
        const orderedGallery = state.gallery;

        if(orderedGallery) {
            orderedGallery.order_by = type;

            state.gallery = orderedGallery;
        }
    },

    setFolderOrder(state, {folder, type}) {
        const folderItem = state.folders.find(item => item.slug == folder);

        if(folderItem) {
            folderItem.order_by = type;
        }
    },

    addPhotoToFolder(state, {folder}) {
        const folderItem = state.folders.find(item => item.slug == folder);

        if(folderItem) {
            folderItem.photos_count++;
        }
    },

    removePhotoFromFolder(state, {folder}) {
        const folderItem = state.folders.find(item => item.slug == folder);

        if(folderItem) {
            folderItem.photos_count--;
        }
    },

    addPhotoToGallery(state, {filesize, count = 1}) {
        if(state.gallery) {
            state.gallery.photos_count += count;
            state.gallery.storage_used = Math.floor(state.gallery.storage_used) + Math.floor(filesize);

            store.commit('user/addStorageUsed', {bytes: filesize});
        }
    },

    removePhotoFromGallery(state, {filesize, count = 1}) {
        if(state.gallery) {
            state.gallery.photos_count -= count;
            state.gallery.storage_used = Math.floor(state.gallery.storage_used) - Math.floor(filesize);

            store.commit('user/addStorageUsed', {bytes: filesize * -1});
        }
    },

    setFavorites(state, {favorites}) {
        state.favorites = favorites;
    },

    addDeletedToGallery(state, {count = 1}) {
        if(state.gallery) {
            state.gallery.deleted_count += count ?? 1;
        }
    },

    removeDeletedFromGallery(state, {count = 1}) {
        if(state.gallery) {
            state.gallery.deleted_count -= count ?? 1;
        }
    },

    clearDeletedFromGallery(state) {
        if(state.gallery) {
            state.gallery.photos_count += state.gallery.deleted_count;
            state.gallery.deleted_count = 0;
        }
    },

    removeDeletedGallery(state, {gallery}) {
        if(state.deletedGalleries.length) {
            state.deletedGalleries = state.deletedGalleries.filter(item => item.slug != gallery);
        }
    },

    setPinnedGallery(state, {is_pinned, gallery}) {
        const galleryItem = state.galleries.find(item => item.slug == gallery);

        if(galleryItem) {
            galleryItem.is_pinned = is_pinned;
        }
    },
};

const actions: ActionTree<State, RootState> & Actions = {
    loadGalleries({commit}: AugmentedActionContext, {page = 1, sort, search, append = false} = {}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            my({page, sort, search}, (data) => {
                if(append) {
                    commit('appendGalleries', {galleries: data.galleries})
                } else {
                    commit('setGalleries', {galleries: data.galleries})
                    commit('setFolders', {folders: []})
                    commit('setFavorites', {favorites: []})
                }

                commit('setGalleriesPagination', {
                    page: data.pagination.current_page,
                    hasMore: data.pagination.has_more,
                    total: data.pagination.total,
                })

                resolve(data.pagination);
            }, (type, data) => {
                reject({type, data});
            })
        });
    },

    loadGalleriesList({commit}: AugmentedActionContext, payload: {}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            list((galleries: GalleryOption[]) => {
                commit('setGalleriesList', {galleries})

                resolve(galleries);
            }, (type, data) => {
                reject({type, data});
            })
        });
    },

    loadDeletedGalleries({commit}: AugmentedActionContext, payload: {}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            deleted((galleries: ShortGallery[]) => {
                commit('setDeletedGalleries', {galleries})

                resolve();
            }, (type, data) => {
                reject({type, data});
            })
        });
    },

    loadGallery({commit}: AugmentedActionContext, {slug}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            details(slug, (gallery) => {
                commit('setGallery', {gallery});

                resolve(gallery);
            }, (type, data) => {
                reject({type, data});
            })
        })
    },

    loadFolders({commit}: AugmentedActionContext, { gallery}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            folders(gallery, (folders) => {
                commit('setFolders', {folders})

                resolve(folders);
            }, (type, data) => {
                reject({type, data});
            })
        })
    },

    sortFolders({commit, getters}: AugmentedActionContext, {gallery, folders}): Promise<any> {
        const tempFolders = getters.folders;

        commit('setFolders', {folders: (folders.length === 0 ? tempFolders : folders)});

        return new Promise((resolve: any, reject) => {
            sortFolders(gallery, folders, () => {
                commit('setFolders', {folders});

                resolve(folders);
            }, (type, data) => {
                commit('setFolders', {folders: tempFolders});

                reject({type, data});
            })
        })
    },

    loadFavorites({commit}: AugmentedActionContext, { gallery }): Promise<any> {
        return new Promise((resolve: any, reject) => {
            favorites(gallery, (favorites) => {
                commit('setFavorites', {favorites});

                resolve(favorites);
            }, (type, data) => {
                reject({type, data});
            })
        });
    },

    deleteGallery({commit}: AugmentedActionContext, {gallery}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            deleteGallery(gallery, () => {
                commit('removeGallery', {gallery});
                commit('removeDeletedGallery', {gallery});

                resolve();
            }, (type, data) => {
                reject({type, data});
            })
        });
    },

    pinGallery({commit}: AugmentedActionContext, {gallery}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            pinGallery(gallery, () => {
                commit('setPinnedGallery', {is_pinned: true, gallery});

                resolve();
            }, (type, data) => {
                reject({type, data});
            });
        });
    },

    unpinGallery({commit}: AugmentedActionContext, {gallery}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            unpinGallery(gallery, () => {
                commit('setPinnedGallery', {is_pinned: false, gallery});

                resolve();
            }, (type, data) => {
                reject({type, data});
            });
        });
    }
};

//setup store type
export type Store<S = State> = Omit<
    VuexStore<S>,
    'commit' | 'getters' | 'dispatch'
    > & {
    commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
        key: K,
        payload: P,
        options?: CommitOptions,
    ): ReturnType<Mutations[K]>
} & {
    getters: {
        [K in keyof Getters]: ReturnType<Getters[K]>
    }
} & {
    dispatch<K extends keyof Actions>(
        key: K,
        payload: Parameters<Actions[K]>[1],
        options?: DispatchOptions,
    ): ReturnType<Actions[K]>
}

export const GalleriesModule: Module<State, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}